import { useFormContext } from '@kanda-libs/ks-component-ts';
import { useToast } from '@kanda-libs/ks-design-library';
import { Document } from '@kanda-libs/ks-frontend-services';
import {
  ChangeEvent,
  DragEvent,
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { CLASS_NAMES } from './constants';
import { convertToBase64, getWrapperClassName } from './helpers';

export interface FileDropClassNames {
  wrapper: string;
  error: string;
  processing: string;
}

export type FileDropShow = 'processing' | 'file' | 'card';

export interface FileDropHookProps {
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  onInputClick: (event: MouseEvent<HTMLInputElement>) => void;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  classNames: FileDropClassNames;
  dragOverError: string | undefined;
  show: FileDropShow;
  finalFile: Document | undefined;
  removeFile: () => void;
}

export default function useFileDrop(
  name: string,
  isSubmitting: boolean,
): FileDropHookProps {
  const [dragOver, setDragOver] = useState<boolean>(false);
  const [dragOverError, setDragOverError] = useState<string | undefined>();
  const [processing, setProcessing] = useState<boolean>(false);

  const { showError } = useToast();
  const { setValue, watch } = useFormContext();

  const finalFile = watch(name);

  const processFile = useCallback(
    async (file: File) => {
      const base64File = await convertToBase64(file);
      setProcessing(false);
      if (base64File.state === 'error') {
        showError(base64File.error);
        return;
      }
      const { state, ...document } = base64File;
      setValue(name, document);
    },
    [showError, name, setValue],
  );

  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
    setDragOverError(undefined);
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragOver(false);
      const files = event?.dataTransfer?.files;
      if (!files || files.length > 1) {
        showError('Please only drop 1 file');
        setDragOverError('Please drop only 1 file');
        return;
      }
      const file = files[0];
      if (file.type !== 'application/pdf') {
        showError('Please drop a PDF file');
        setDragOverError('Please drop a PDF file');
        return;
      }
      processFile(file);
    },
    [showError, processFile],
  );

  const onInputClick = useCallback((event: MouseEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    if (!target) return;
    target.value = '';
  }, []);

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event?.target?.files;
      if (!files || files.length > 1) {
        showError('Please only select 1 file');
        setDragOverError('Please select only 1 file');
        return;
      }
      const file = files[0];
      if (file.type !== 'application/pdf') {
        showError('Please drop a PDF file');
        setDragOverError('Please drop a PDF file');
        return;
      }
      processFile(file);
    },
    [showError, processFile],
  );

  const classNames = useMemo(
    () => ({
      ...CLASS_NAMES,
      wrapper: [
        CLASS_NAMES.wrapper,
        getWrapperClassName(dragOver, Boolean(dragOverError)),
      ].join(' '),
    }),
    [dragOver, dragOverError],
  );

  const show = useMemo(() => {
    if (processing || isSubmitting) return 'processing';
    if (finalFile) return 'file';
    return 'card';
  }, [processing, finalFile, isSubmitting]);

  const removeFile = useCallback(
    () => setValue(name, undefined),
    [setValue, name],
  );

  return {
    classNames,
    onDragOver,
    onDragLeave,
    onDrop,
    onInputClick,
    onInputChange,
    dragOverError,
    show,
    finalFile,
    removeFile,
  };
}
